import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { uploadBlob, captureVideoPoster } from "../lib/media";
import Navbar from "../components/Navbar";
import AuthMedia from "../components/AuthMedia";
import VideoRecorder from "../components/VideoRecorder";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Trash2, Upload, X, Loader2, Save, Video, Music4, Users, Image as ImageIcon, CalendarClock, Paperclip } from "lucide-react";
import { toast } from "sonner";

const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return ""; }
};

const MemoryEditor = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const location = useLocation();
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const songRef = useRef(null);
  const [uploadingSong, setUploadingSong] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [form, setForm] = useState({ title: isNew && location.state?.prompt ? location.state.prompt : "", content: "", media: [], song: null });

  useEffect(() => {
    if (isNew) return;
    api.get(`/memories/${id}`).then((res) => {
      const d = res.data;
      setForm({ title: d.title || "", content: d.content || "", media: d.media || [], song: d.song || null });
      setCreatedAt(d.created_at || null);
    }).catch(() => toast.error("Ricordo non trovato")).finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const data = await uploadBlob(file, file.name);
        if (data.kind === "video") {
          const posterBlob = await captureVideoPoster(file);
          if (posterBlob) {
            const p = await uploadBlob(posterBlob, `poster-${Date.now()}.jpg`);
            data.poster = p.storage_path;
          }
        }
        setForm((f) => ({ ...f, media: [...f.media, data] }));
      }
      toast.success("Allegato caricato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally {
      setUploading(false);
      [fileRef, photoRef, videoRef].forEach((r) => { if (r.current) r.current.value = ""; });
    }
  };

  const removeMedia = (path) => set("media", form.media.filter((m) => m.storage_path !== path));

  const handleSong = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploadingSong(true);
    try {
      const data = await uploadBlob(file, file.name);
      setForm((f) => ({ ...f, song: data }));
      toast.success("Canzone allegata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally {
      setUploadingSong(false);
      if (songRef.current) songRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Aggiungi un titolo");
    setSaving(true);
    try {
      // Every memory in the book is revealed to ALL people in the list at death.
      const payload = { ...form, release_type: "death", release_date: null, event_description: null, recipients: [], heir_ids: [] };
      if (isNew) {
        await api.post("/memories", payload);
        toast.success("Ricordo custodito");
      } else {
        await api.put(`/memories/${id}`, payload);
        toast.success("Modifiche salvate");
      }
      navigate("/ricordi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo ricordo?")) return;
    await api.delete(`/memories/${id}`);
    toast.success("Ricordo eliminato");
    navigate("/ricordi");
  };

  if (loading) return (<div className="min-h-screen"><Navbar /><p className="max-w-3xl mx-auto px-6 py-10 text-muted-foreground">Caricamento…</p></div>);

  const images = form.media.filter((m) => m.kind === "image");
  const videos = form.media.filter((m) => m.kind === "video");
  const others = form.media.filter((m) => m.kind !== "image" && m.kind !== "video");

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-8 fade-in">
        <button onClick={() => navigate("/ricordi")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#2C2A29] mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Torna al libro
        </button>

        <div className="bg-white rounded-3xl border border-border p-8 space-y-8">
          <div>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Titolo del ricordo…"
              className="font-serif text-2xl border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent" data-testid="memory-title-input" />
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5" data-testid="memory-created-at">
              <CalendarClock className="w-3.5 h-3.5" />
              {createdAt ? `Ricordo creato il ${fmtDay(createdAt)}` : "Nuovo ricordo · non ancora salvato"}
            </p>
          </div>

          <div>
            <Label className="mb-2 block">Il tuo messaggio</Label>
            <Textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={6}
              placeholder="Scrivi una lettera, un pensiero, un consiglio di vita…"
              className="bg-[#F4F1EA] border-border rounded-xl resize-none" data-testid="memory-content-input" />
          </div>

          {/* Foto */}
          <div data-testid="editor-photos">
            <Label className="mb-3 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#81B29A]" /> Foto</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.map((m) => (
                <div key={m.storage_path} className="relative group rounded-xl overflow-hidden border border-border bg-[#F4F1EA]">
                  <AuthMedia item={m} className="w-full h-28 object-cover" />
                  <button onClick={() => removeMedia(m.storage_path)} className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 shadow hover:bg-destructive hover:text-white transition-colors" data-testid="media-remove-btn">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => photoRef.current?.click()} disabled={uploading}
                className="h-28 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-testid="photo-upload-btn">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                <span className="text-xs">Aggiungi foto</span>
              </button>
            </div>
            <input ref={photoRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="photo-file-input" />
          </div>

          {/* Videomessaggi */}
          <div data-testid="editor-videos">
            <Label className="mb-3 flex items-center gap-2"><Video className="w-4 h-4 text-primary" /> Videomessaggi</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {videos.map((m) => (
                <div key={m.storage_path} className="relative group rounded-xl overflow-hidden border border-border bg-[#F4F1EA]">
                  <AuthMedia item={m} className="w-full h-28 object-cover" />
                  <button onClick={() => removeMedia(m.storage_path)} className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 shadow hover:bg-destructive hover:text-white transition-colors" data-testid="media-remove-btn">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  {(m.uploaded_at || createdAt) && (
                    <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-2 py-1 flex items-center gap-1" data-testid="video-uploaded-at">
                      <CalendarClock className="w-3 h-3" /> Caricato il {fmtDay(m.uploaded_at || createdAt)}
                    </span>
                  )}
                </div>
              ))}
              <button onClick={() => videoRef.current?.click()} disabled={uploading}
                className="h-28 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-testid="video-upload-btn">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                <span className="text-xs">Carica video</span>
              </button>
            </div>
            <input ref={videoRef} type="file" accept="video/*" multiple onChange={handleUpload} className="hidden" data-testid="video-file-input" />
            <button onClick={() => setShowRecorder(true)} className="mt-3 inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="open-recorder-btn">
              <Video className="w-4 h-4" /> Registra un videomessaggio guidato
            </button>
            <VideoRecorder open={showRecorder} onOpenChange={setShowRecorder} onSave={(item) => setForm((f) => ({ ...f, media: [...f.media, item] }))} />
          </div>

          {/* Voci e documenti */}
          <div data-testid="editor-others">
            <Label className="mb-3 flex items-center gap-2"><Paperclip className="w-4 h-4 text-[#8A6D9C]" /> Voci e documenti</Label>
            {others.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                {others.map((m) => (
                  <div key={m.storage_path} className="relative rounded-xl border border-border bg-[#F4F1EA] p-3 pr-9">
                    <AuthMedia item={m} />
                    <button onClick={() => removeMedia(m.storage_path)} className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 shadow hover:bg-destructive hover:text-white transition-colors" data-testid="media-remove-btn">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="media-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Aggiungi una voce o un documento
            </button>
            <input ref={fileRef} type="file" accept="audio/*,application/pdf" multiple onChange={handleUpload} className="hidden" data-testid="media-file-input" />
          </div>

          {/* Song dedication */}
          <div>
            <Label className="mb-2 block">Dedica musicale (facoltativa)</Label>
            {form.song ? (
              <div className="flex items-center gap-3 bg-[#F0EAF4] rounded-xl p-4" data-testid="song-attached">
                <Music4 className="w-5 h-5 text-[#8A6D9C] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{form.song.original_filename}</p>
                  <div className="mt-2"><AuthMedia item={form.song} /></div>
                </div>
                <Button variant="ghost" onClick={() => set("song", null)} className="text-destructive hover:bg-destructive/10 rounded-full" data-testid="song-remove-btn"><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <button onClick={() => songRef.current?.click()} disabled={uploadingSong} className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="song-add-btn">
                {uploadingSong ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music4 className="w-4 h-4" />} Abbina una canzone a questo ricordo
              </button>
            )}
            <input ref={songRef} type="file" accept="audio/*" onChange={handleSong} className="hidden" data-testid="song-file-input" />
          </div>

          {/* Visibility info */}
          <div className="flex items-start gap-3 bg-[#EAF3EE] border border-[#81B29A]/40 rounded-xl p-4" data-testid="memory-visibility-note">
            <Users className="w-5 h-5 text-[#4e8f6f] mt-0.5 shrink-0" />
            <p className="text-sm text-[#3d3a38]">Questo ricordo verrà svelato a <span className="font-medium">tutte le persone della tua lista</span> al momento della verifica ufficiale della scomparsa. Riceveranno via email il link per accedere.</p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            {!isNew ? (
              <Button variant="ghost" onClick={remove} className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full" data-testid="memory-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Elimina
              </Button>
            ) : <span />}
            <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-[#c9694f] text-white h-11 px-8" data-testid="memory-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {isNew ? "Custodisci" : "Salva"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MemoryEditor;
